import { Alert, Button, Flex, Skeleton, Text } from '@backstage/ui';
import {
  type IdentityProviders,
  SignInPage,
  UserIdentity,
} from '@backstage/core-components';
import {
  configApiRef,
  oktaAuthApiRef,
  useAnalytics,
  useApi,
} from '@backstage/core-plugin-api';
import type { SignInPageProps } from '@backstage/plugin-app-react';
import { RiArrowRightLongLine } from '@remixicon/react';
import clsx from 'clsx';
import { useCallback, useEffect, useState } from 'react';

import styles from './AuraSignInPage.module.css';

// Backstage reads this key itself to restore the session on reload, so the
// storage API cannot replace localStorage here.
const PROVIDER_STORAGE_KEY = '@backstage/core:SignInPage:provider';

const ERROR_MESSAGE =
  'Sign-in failed. Your Okta session may have expired or access was denied.';

const INTERACTIVE_SELECTOR =
  'button, a, input, select, textarea, [tabindex]:not([tabindex="-1"])';

// The session check usually finishes in under a second, so a skeleton that
// shows at once flashes on every reload of a signed-in page.
const SKELETON_DELAY_MS = 1000;

export const AuraSignInPage = (props: SignInPageProps) => {
  const configApi = useApi(configApiRef);
  if (configApi.getString('auth.environment') === 'ci') {
    const providers: IdentityProviders = ['guest'];
    return <SignInPage {...props} providers={providers} align="center" />;
  }
  return <AuraOktaSignInPage {...props} />;
};

function AuraOktaSignInPage({ onSignInSuccess }: SignInPageProps) {
  const oktaAuthApi = useApi(oktaAuthApiRef);
  const analytics = useAnalytics();
  const [checking, setChecking] = useState(
    () => localStorage.getItem(PROVIDER_STORAGE_KEY) !== null,
  );
  const [skeletonVisible, setSkeletonVisible] = useState(false);
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [keyDown, setKeyDown] = useState(false);

  useEffect(() => {
    if (!checking) {
      return undefined;
    }
    let cancelled = false;
    oktaAuthApi
      .getBackstageIdentity({ optional: true })
      .then(async response => {
        if (cancelled) {
          return;
        }
        if (!response) {
          localStorage.removeItem(PROVIDER_STORAGE_KEY);
          setChecking(false);
          return;
        }
        const profile = await oktaAuthApi.getProfile();
        onSignInSuccess(
          UserIdentity.create({
            identity: response.identity,
            profile,
            authApi: oktaAuthApi,
          }),
        );
        analytics.captureEvent('signIn', 'success');
      })
      .catch(() => {
        localStorage.removeItem(PROVIDER_STORAGE_KEY);
        if (!cancelled) {
          setChecking(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [analytics, checking, oktaAuthApi, onSignInSuccess]);

  useEffect(() => {
    if (!checking) {
      return undefined;
    }
    const timer = window.setTimeout(
      () => setSkeletonVisible(true),
      SKELETON_DELAY_MS,
    );
    return () => window.clearTimeout(timer);
  }, [checking]);

  const signIn = useCallback(async () => {
    if (pending) {
      return;
    }
    setPending(true);
    setFailure(null);
    localStorage.setItem(PROVIDER_STORAGE_KEY, 'okta-auth-provider');
    try {
      const response = await oktaAuthApi.getBackstageIdentity({
        instantPopup: true,
      });
      if (!response) {
        throw new Error(
          'The Okta provider is not configured to support sign in',
        );
      }
      const profile = await oktaAuthApi.getProfile();
      onSignInSuccess(
        UserIdentity.create({
          identity: response.identity,
          profile,
          authApi: oktaAuthApi,
        }),
      );
      analytics.captureEvent('signIn', 'success');
    } catch (error) {
      localStorage.removeItem(PROVIDER_STORAGE_KEY);
      setFailure(failureMessage(error));
      setPending(false);
    }
  }, [analytics, oktaAuthApi, onSignInSuccess, pending]);

  useEffect(() => {
    if (checking || pending) {
      return undefined;
    }
    let timer: number | undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Enter' || event.repeat) {
        return;
      }
      setKeyDown(true);
      timer = window.setTimeout(() => setKeyDown(false), 140);
      if (event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }
      if (
        event.target instanceof Element &&
        event.target.closest(INTERACTIVE_SELECTOR)
      ) {
        return;
      }
      event.preventDefault();
      void signIn();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (timer !== undefined) {
        window.clearTimeout(timer);
      }
    };
  }, [checking, pending, signIn]);

  const hintVisible = !checking && !pending && !failure;

  let buttonLabel = 'Sign in with Okta';
  if (pending) {
    buttonLabel = 'Signing in…';
  } else if (failure) {
    buttonLabel = 'Try again';
  }

  const sessionCheckIndicator = skeletonVisible ? (
    <Skeleton
      className={styles.signInButton}
      height="2.5rem"
      role="status"
      aria-label="Checking your session"
    />
  ) : (
    <div className={clsx(styles.signInButton, styles.skeletonSpace)} />
  );

  return (
    <Flex
      className={styles.root}
      direction="column"
      align="center"
      justify="center"
      gap="0"
      p="6"
      aria-busy={checking || pending}
    >
      <div className={styles.field} aria-hidden="true">
        <span className={clsx(styles.blob, styles.blobA)} />
        <span className={clsx(styles.blob, styles.blobB)} />
        <span className={clsx(styles.blob, styles.blobC)} />
      </div>
      <Flex
        className={styles.content}
        direction="column"
        align="center"
        gap="0"
      >
        <span className={styles.mark} aria-hidden="true" />
        <Text as="h1" variant="title-medium" weight="bold">
          Aura Portal
        </Text>
        <Text
          as="p"
          className={styles.subtitle}
          variant="body-medium"
          color="secondary"
        >
          Neo4j internal developer portal
        </Text>
        {checking ? (
          sessionCheckIndicator
        ) : (
          <>
            {failure && (
              // BUI Alert sets no live region. role="alert" makes screen readers
              // announce the failure.
              <Alert
                className={styles.alert}
                role="alert"
                status="danger"
                icon
                title="Failed to sign in"
                description={failure}
                mb="5"
              />
            )}
            <Button
              className={styles.signInButton}
              variant="primary"
              size="medium"
              onPress={signIn}
              isPending={pending}
              iconEnd={
                pending ? undefined : (
                  <RiArrowRightLongLine className={styles.arrow} />
                )
              }
            >
              {buttonLabel}
            </Button>
          </>
        )}
        <Flex
          className={clsx(!hintVisible && styles.hintHidden)}
          align="center"
          gap="2"
          mt="4"
        >
          <kbd className={clsx(styles.kbd, keyDown && styles.kbdDown)}>↵</kbd>
          <Text as="span" variant="body-small" weight="bold" color="secondary">
            Press Enter to sign in
          </Text>
        </Flex>
      </Flex>
    </Flex>
  );
}

// The user closed the Okta popup. The Backstage sign-in page shows no error
// for this case either.
function failureMessage(error: unknown) {
  if (error instanceof Error && error.name === 'PopupRejectedError') {
    return null;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return ERROR_MESSAGE;
}
